import { computed, ref, shallowRef } from 'vue'
import { createAuth0, useAuth0 as useAuth0Vue, authGuard as auth0Guard } from '@auth0/auth0-vue'
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { isKeycloak, resolveAuthProvider } from './provider.js'

// The active provider is decided at runtime (see provider.js).
export { isKeycloak, resolveAuthProvider }

/**
 * Vue admin auth facade — Auth0 or Keycloak, chosen per environment.
 *
 * Exposes exactly the surface the admin app already uses:
 *   useAuth0() -> { user, isAuthenticated, isLoading, getAccessTokenSilently,
 *                   loginWithRedirect, logout }
 *   authGuard  -> vue-router navigation guard
 *   createAuthPlugin() -> the app.use(...) plugin
 *
 * Keeping the `useAuth0` name means the 8 components that consume it need only an
 * import-path change, not a rewrite.
 *
 * NOTE: the admin portal is for MANAGERS. It deliberately has no org-scoped login and
 * no player OTP plane — managers always authenticate against the IdP with a password
 * (and MFA where the org policy requires it).
 */

// Provider-specific (never a fallback chain): with both providers configured, a chain would hand
// the Auth0 SDK the Keycloak issuer or client id.
const issuer = () => {
    if (isKeycloak()) return String(process.env.VUE_APP_AUTH_ISSUER || '').replace(/\/$/, '')
    const domain = process.env.VUE_APP_AUTH0_DOMAIN
    return domain ? `https://${domain}` : ''
}
const clientId = () => (isKeycloak() ? process.env.VUE_APP_AUTH_CLIENTID : process.env.VUE_APP_AUTH0_CLIENTID) || ''
const audience = () => process.env.VUE_APP_AUTH_AUDIENCE || 'hasura'
const redirectUri = () => `${window.location.origin}/admin`

// ---------------------------------------------------------------------------
// Keycloak (OIDC) adapter
// ---------------------------------------------------------------------------
// One module-level UserManager and one reactive state object, created lazily on
// install. Vue's Auth0 plugin exposes refs, so this mirrors that shape rather than
// forcing callers to await promises.
let userManager = null
const state = {
    user: shallowRef(null),
    isAuthenticated: ref(false),
    isLoading: ref(true),
    error: shallowRef(null)
}

const buildUserManager = () => new UserManager({
    authority: issuer(),
    client_id: clientId(),
    redirect_uri: redirectUri(),
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    scope: 'openid profile email',
    extraQueryParams: { audience: audience() },
    automaticSilentRenew: true,
    // sessionStorage, not the default localStorage: the admin portal holds the most
    // privileged sessions on the platform, so the session should not outlive the tab.
    userStore: new WebStorageStateStore({ store: window.sessionStorage })
})

/**
 * The ID-token claims, flattened onto the user object so `user.value['...']` reads the
 * same way it does under the Auth0 plugin.
 */
const applyUser = (oidcUser) => {
    state.user.value = oidcUser ? { ...oidcUser.profile, sub: oidcUser.profile?.sub, email: oidcUser.profile?.email } : null
    state.isAuthenticated.value = Boolean(oidcUser && !oidcUser.expired)
    state.isLoading.value = false
}

const keycloakPlugin = {
    async install(app) {
        userManager = buildUserManager()

        userManager.events.addUserLoaded(applyUser)
        userManager.events.addUserUnloaded(() => applyUser(null))
        // A failed silent renew means the session is gone; reflect that rather than
        // leaving the UI believing it is still authenticated with a dead token.
        userManager.events.addSilentRenewError(() => applyUser(null))

        try {
            // Complete the redirect if we are landing on the callback, otherwise pick
            // up any stored session.
            if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
                const loggedIn = await userManager.signinRedirectCallback()
                applyUser(loggedIn)
                window.history.replaceState({}, document.title, window.location.pathname)
            } else {
                applyUser(await userManager.getUser())
            }
        } catch (err) {
            state.error.value = err
            state.isLoading.value = false
        }

        app.config.globalProperties.$auth = keycloakAuth()
    }
}

const keycloakAuth = () => ({
    user: state.user,
    isAuthenticated: computed(() => state.isAuthenticated.value),
    isLoading: computed(() => state.isLoading.value),
    error: computed(() => state.error.value),
    getAccessTokenSilently: async () => {
        const current = await userManager.getUser()
        if (current && !current.expired) return current.access_token
        const renewed = await userManager.signinSilent().catch(() => null)
        if (renewed?.access_token) return renewed.access_token
        throw new Error('Unable to obtain an access token')
    },
    loginWithRedirect: async (opts = {}) =>
        userManager.signinRedirect({
            state: opts.appState,
            login_hint: opts.authorizationParams?.login_hint
        }),
    logout: async (opts = {}) =>
        userManager.signoutRedirect({
            post_logout_redirect_uri: opts.logoutParams?.returnTo ?? window.location.origin
        })
})

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

export const createAuthPlugin = () => {
    if (isKeycloak()) return keycloakPlugin
    return createAuth0({
        domain: issuer().replace(/^https?:\/\//, ''),
        clientId: clientId(),
        authorizationParams: {
            redirect_uri: redirectUri(),
            audience: audience()
            // No `organization` here — the Vue admin is used by managers who work
            // across all orgs. Org-scoped login (SSO/MFA) is handled in the React
            // client, where sponsors and players are org-specific.
        }
    })
}

export const useAuth0 = () => (isKeycloak() ? keycloakAuth() : useAuth0Vue())

/**
 * Router guard for protected routes.
 *
 * The Auth0 plugin ships its own; the Keycloak branch reproduces the same contract —
 * wait for the initial session resolution, then either allow or start a redirect.
 */
export const authGuard = async (to, from, next) => {
    if (!isKeycloak()) return auth0Guard(to, from, next)

    // The plugin's install() resolves isLoading before the first navigation in
    // practice, but a deep link can race it; poll briefly rather than flashing the
    // login redirect at an already-authenticated user.
    const deadline = Date.now() + 5000
    while (state.isLoading.value && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 50))
    }

    if (state.isAuthenticated.value) return next()
    await keycloakAuth().loginWithRedirect({ appState: { returnTo: to.fullPath } })
    return undefined
}

/** End-session URL, used by the access-denied interceptor before the plugin mounts. */
export const logoutUrlFor = (returnTo) => {
    const id = encodeURIComponent(clientId())
    const target = encodeURIComponent(returnTo)
    return isKeycloak()
        ? `${issuer()}/protocol/openid-connect/logout?client_id=${id}&post_logout_redirect_uri=${target}`
        : `${issuer()}/v2/logout?client_id=${id}&returnTo=${target}`
}
